import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { formatPrice, calcDiscount } from '../../utils/formatPrice';
import { useMarket } from '../../context/MarketContext';
import { getBestsellers } from '../../services/productService';
import QuantitySelector from '../common/QuantitySelector';
import { getAvailableCoupons } from '../../api/real/couponsApi';
import { useAuth } from '../../context/AuthContext';
import { calculateLoyaltyDiscount } from '../../api/real/loyaltyApi';
import { OfferNotice, OfferItemTag } from './OfferNotice';
import { useShippingDefaults } from '../../services/shippingConfigService';

/* Small upsell product tile */
const UpsellTile = ({ product, onAdd, inCart, fmtPrice }) => (
  <div className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg border border-border">
    <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface shrink-0 border border-border">
      <img src={product.images[0]} alt={product.title} className="w-full h-full object-contain p-0.5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] text-ink-muted uppercase tracking-wide font-bold">{product.vendor}</p>
      <p className="text-xs font-medium text-ink line-clamp-1">{product.title}</p>
      <p className="text-xs font-bold text-primary mt-0.5">{fmtPrice(product.marketPrice ?? product.price)}</p>
    </div>
    <button
      onClick={() => onAdd(product)}
      disabled={inCart}
      className={`shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition-all
        ${inCart ? 'border-emerald-400 text-emerald-600 bg-emerald-50 cursor-default'
                 : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
    >
      {inCart ? '✓ Added' : '+ Add'}
    </button>
  </div>
);

/* Available coupon card */
const CouponCard = ({ coupon, onApply, isApplied, fmtPrice }) => {
  const isNudge = !!coupon.nudgeMessage;
  return (
    <div className={`flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all
      ${isNudge ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono font-bold text-xs text-ink">{coupon.code}</span>
          {coupon.paymentMode && coupon.paymentMode !== 'ALL' && (
            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
              {coupon.paymentMode === 'PREPAID' ? 'Prepaid only' : 'COD only'}
            </span>
          )}
          {coupon.autoApply && (
            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">Auto</span>
          )}
        </div>
        <p className="text-[11px] text-ink-soft mt-0.5 line-clamp-1">
          {isNudge ? coupon.nudgeMessage : (coupon.shortDescription || `Save ${fmtPrice(coupon.discountAmount || 0)}`)}
        </p>
      </div>
      {!isNudge && (
        <button
          onClick={() => onApply(coupon.code)}
          disabled={isApplied}
          className={`shrink-0 ml-2 text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-all
            ${isApplied ? 'border-emerald-400 text-emerald-600 bg-white cursor-default'
                        : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
        >
          {isApplied ? '✓' : 'Apply'}
        </button>
      )}
    </div>
  );
};

/* Coupon section component */
const CouponSection = ({ subtotal }) => {
  const { applyCoupon, removeCoupon, appliedCoupon, couponError, couponLoading, itemCount, buildCouponItems } = useCart();
  const { currentMarket } = useMarket();
  const fmtPrice = useCallback((amount) => {
    const symbol = currentMarket?.currencySymbol;
    if (symbol && symbol !== '₹') {
      return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return formatPrice(amount);
  }, [currentMarket]);
  const [input, setInput] = useState('');
  const [showBrowser, setShowBrowser] = useState(false);
  const [available, setAvailable] = useState({ applicable: [], almostApplicable: [], autoApply: [] });
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  const loadAvailable = useCallback(async () => {
    setLoadingAvailable(true);
    try {
      const data = await getAvailableCoupons({
        cartSubtotal: subtotal,
        cartItemCount: itemCount,
        items: buildCouponItems(),
        marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
      });
      setAvailable(data || { applicable: [], almostApplicable: [], autoApply: [] });
    } catch {
      // silent — don't break cart
    } finally {
      setLoadingAvailable(false);
    }
  }, [subtotal, itemCount, buildCouponItems, currentMarket]);

  useEffect(() => {
    if (showBrowser) loadAvailable();
  }, [showBrowser, loadAvailable]);

  const handleApply = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const code = typeof e === 'string' ? e : input;
    const ok = await applyCoupon(code, {
      cartSubtotal: subtotal,
      marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
    });
    if (ok) { setInput(''); setShowBrowser(false); }
  };

  if (appliedCoupon) {
    return (
      <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
          </svg>
          <div className="min-w-0">
            <p className="text-xs font-bold text-emerald-700">{appliedCoupon.code}</p>
            <p className="text-[11px] text-emerald-600 truncate">{appliedCoupon.message}</p>
            {appliedCoupon.requiredPaymentMode === 'PREPAID' && (
              <p className="text-[10px] text-blue-600 font-medium mt-0.5">⚡ Prepaid payment required</p>
            )}
          </div>
        </div>
        <button onClick={removeCoupon} className="shrink-0 text-[11px] text-emerald-600 hover:text-red-500 font-medium ml-2 transition-colors">
          Remove
        </button>
      </div>
    );
  }

  const allCoupons = [
    ...(available.applicable || []),
    ...(available.almostApplicable || []),
  ];

  return (
    <div className="space-y-2">
      <form onSubmit={handleApply} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase())}
          placeholder="Enter coupon code"
          className="flex-1 px-3 py-2 text-xs border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white placeholder:text-ink-muted uppercase"
          disabled={couponLoading}
        />
        <button
          type="submit"
          disabled={couponLoading || !input.trim()}
          className="px-3.5 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 shrink-0"
        >
          {couponLoading ? '…' : 'Apply'}
        </button>
      </form>

      {couponError && (
        <div className="flex items-start gap-1.5 px-1">
          <svg className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p className="text-[11px] text-red-500">{couponError}</p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowBrowser(o => !o)}
        className="flex items-center gap-1.5 text-[11px] text-primary font-medium hover:underline w-full"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
        </svg>
        {showBrowser ? 'Hide coupons' : 'View available coupons'}
        <svg className={`w-3 h-3 transition-transform ${showBrowser ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>

      {showBrowser && (
        <div className="space-y-1.5 max-h-44 overflow-y-auto pr-0.5">
          {loadingAvailable ? (
            <p className="text-[11px] text-ink-muted text-center py-2">Loading coupons…</p>
          ) : allCoupons.length === 0 ? (
            <p className="text-[11px] text-ink-muted text-center py-2">No coupons available right now</p>
          ) : (
            allCoupons.map(c => (
              <CouponCard
                key={c.code}
                coupon={c}
                isApplied={appliedCoupon?.code === c.code}
                onApply={handleApply}
                fmtPrice={fmtPrice}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CartDrawer = () => {
  const {
    items, itemCount, subtotal,
    removeItem, updateQuantity, addItem,
    isDrawerOpen, setIsDrawerOpen,
    appliedCoupon, couponDiscount,
    offer, offerDiscount, offerForItem,
    openCheckout,
  } = useCart();
  const navigate = useNavigate();
  const { currentMarket } = useMarket();
  const { user } = useAuth();
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);

  // Market-aware price formatter: uses market currency symbol when not INR
  const fmtPrice = useCallback((amount) => {
    const symbol = currentMarket?.currencySymbol;
    if (symbol && symbol !== '₹') {
      return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return formatPrice(amount);
  }, [currentMarket]);

  const { fee: shippingFee, threshold: freeThreshold } = useShippingDefaults(currentMarket, 80, 699);
  // like the server: a free item does not count towards free shipping
  const pricedSubtotal = Math.max(0, subtotal - offerDiscount);
  const shipping = pricedSubtotal >= freeThreshold ? 0 : shippingFee;
  const total = Math.max(0, subtotal - offerDiscount - couponDiscount + shipping);
  const totalSavings = items.reduce((s, i) => {
    const diff = (i.compareAtPrice || i.price) - i.price;
    return s + (diff > 0 ? diff * i.quantity : 0);
  }, 0);

  const cartIds = new Set(items.map(i => i.id));
  const [upsells, setUpsells] = useState([]);
  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;

    const prepaidDiscountPreview = (() => {
    if (!currentMarket) return 0;
    const percent = currentMarket.prepaidDiscountPercent;
    const flat    = currentMarket.prepaidDiscountFlat;
    const applyMax = currentMarket.prepaidApplyMaximum;
    const minOV   = currentMarket.prepaidMinOrderValue;
    const maxOV   = currentMarket.prepaidMaxOrderValue;
    if ((!percent || Number(percent) === 0) && (!flat || Number(flat) === 0)) return 0;
    if (minOV != null && subtotal < Number(minOV)) return 0;
    if (maxOV != null && subtotal > Number(maxOV)) return 0;
    const pctDiscount = percent ? Math.round((subtotal * Number(percent) / 100) * 100) / 100 : 0;
    if (!flat || Number(flat) === 0) return Math.max(0, pctDiscount);
    const flatNum = Number(flat);
    return Math.max(0, applyMax ? Math.max(pctDiscount, flatNum) : Math.min(pctDiscount, flatNum));
  })();

  useEffect(() => {
    getBestsellers(6, marketCode).then(list => {
      setUpsells(list.filter(p => !cartIds.has(p.id)).slice(0, 3));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen]);

  // Fetch loyalty earn config for logged-in users
  useEffect(() => {
    if (!user || !isDrawerOpen || subtotal <= 0) return;
    calculateLoyaltyDiscount(subtotal)
      .then(data => setLoyaltyInfo(data))
      .catch(() => {});
  }, [user, isDrawerOpen, subtotal]);

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isDrawerOpen]);

  if (!isDrawerOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in" onClick={() => setIsDrawerOpen(false)} />

      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-white z-50 flex flex-col shadow-2xl animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-base font-bold text-ink">Shopping Cart</h2>
            {itemCount > 0 && <p className="text-xs text-ink-soft">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>}
          </div>
          <button onClick={() => setIsDrawerOpen(false)}
            className="w-8 h-8 rounded-lg bg-surface hover:bg-primary-light flex items-center justify-center text-ink-soft hover:text-primary transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Free shipping progress */}
        {items.length > 0 && pricedSubtotal < freeThreshold && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 shrink-0">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-amber-700 font-medium">🚚 Add {fmtPrice(freeThreshold - pricedSubtotal)} more for FREE shipping!</span>
              <span className="text-amber-600 font-semibold">{Math.round((pricedSubtotal / freeThreshold) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                   style={{ width: `${Math.min(100, (pricedSubtotal / freeThreshold) * 100)}%` }} />
            </div>
          </div>
        )}
        {items.length > 0 && pricedSubtotal >= freeThreshold && (
          <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 shrink-0 text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
            </svg>
            You've unlocked FREE shipping! 🎉
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-20 h-20 rounded-full bg-primary-light flex items-center justify-center mb-4">
                <svg className="w-9 h-9 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 10H4L5 9z"/>
                </svg>
              </div>
              <h3 className="text-base font-bold text-ink mb-1">Your cart is empty</h3>
              <p className="text-sm text-ink-soft mb-6">Add derma-approved products to get started</p>
              <button onClick={() => { setIsDrawerOpen(false); navigate('/shop'); }} className="btn-primary btn-sm">
                Shop Now →
              </button>
            </div>
          ) : (
            <>
              <OfferNotice offer={offer} fmt={fmtPrice} className="mx-5 mt-4" />
              <ul className="px-5 py-4 space-y-4">
                {items.map((item, idx) => {
                  const discount = calcDiscount(item.price, item.compareAtPrice);
                  return (
                    <li key={`${item.id}-${item.selectedVariant}-${idx}`}
                      className="flex gap-3 pb-4 border-b border-border/60 last:border-0 last:pb-0">
                      <Link to={`/product/${item.handle}`} onClick={() => setIsDrawerOpen(false)} className="shrink-0">
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-surface border border-border">
                          <img
                            src={item.imageUrl || item.images?.[0]}
                            alt={item.title}
                            className="w-full h-full object-contain p-1"
                            onError={e => { e.target.src = '/placeholder-product.png'; }}
                          />
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-primary">{item.vendor}</span>
                        <Link to={`/product/${item.handle}`} onClick={() => setIsDrawerOpen(false)}
                          className="block text-sm font-medium text-ink line-clamp-2 hover:text-primary transition-colors leading-snug mt-0.5">
                          {item.title}
                        </Link>
                        {item.selectedVariant && item.selectedVariant !== 'default' && (
                          <p className="text-xs text-ink-muted mt-0.5">{item.selectedVariant}</p>
                        )}
                        <OfferItemTag line={offerForItem(item)} unitPrice={item.price} fmt={fmtPrice} />
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5">
                          <QuantitySelector value={item.quantity} onChange={q => updateQuantity(item.id, item.selectedVariant, q)} min={0} />
                          <div className="text-right">
                            <p className="text-sm font-bold text-ink">{fmtPrice(item.price * item.quantity)}</p>
                            {item.quantity > 1 && <p className="text-[11px] text-ink-muted">{fmtPrice(item.price)} each</p>}
                            {discount > 0 && <p className="text-[11px] text-emerald-600 font-semibold">-{discount}%</p>}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => removeItem(item.id, item.selectedVariant)}
                        className="shrink-0 self-start mt-1 p-1.5 text-ink-muted hover:text-red-400 hover:bg-red-50 rounded-lg transition-all">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Upsell section */}
              {upsells.length > 0 && (
                <div className="px-5 pb-4">
                  <div className="bg-primary-light rounded-xl p-3.5">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">✨ Boost Your Results</p>
                    <div className="space-y-2">
                      {upsells.map(p => (
                        <UpsellTile key={p.id} product={p} inCart={cartIds.has(p.id)}
                          onAdd={prod => addItem(prod, prod.variants[0]?.size || 'default')} fmtPrice={fmtPrice} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-5 py-4 border-t border-border shrink-0 bg-surface">
            {/* Coupon */}
            <div className="mb-3">
              <CouponSection subtotal={subtotal} />
            </div>

            <div className="space-y-2 mb-4 text-sm">
              <div className="flex justify-between text-ink-soft">
                <span>Subtotal ({itemCount} items)</span>
                <span className="font-semibold text-ink">{fmtPrice(subtotal)}</span>
              </div>
              {totalSavings > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span className="font-medium">Product savings</span>
                  <span className="font-bold">-{fmtPrice(totalSavings)}</span>
                </div>
              )}
              {offerDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span className="font-medium">Offer · {offer.title}</span>
                  <span className="font-bold">-{fmtPrice(offerDiscount)}</span>
                </div>
              )}
              {couponDiscount > 0 && appliedCoupon && (
                <div className="flex justify-between text-emerald-600">
                  <span className="font-medium">Coupon ({appliedCoupon.code})</span>
                  <span className="font-bold">-{fmtPrice(couponDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-ink-soft">
                <span>Shipping</span>
                <span className={`font-semibold ${shipping === 0 ? 'text-emerald-600' : 'text-ink'}`}>
                  {shipping === 0 ? 'FREE' : fmtPrice(shipping)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-base text-ink border-t border-border pt-2 mt-1">
                <span>Total</span>
                <span className="text-primary">{fmtPrice(total)}</span>
              </div>
            </div>

            {/* Loyalty earn preview */}
            {(() => {
              if (!user || !loyaltyInfo?.enabled || !loyaltyInfo?.earnEnabled) return null;
              const earnRate = Number(loyaltyInfo.earnRate || 1);
              const maxEarn  = Number(loyaltyInfo.maxEarnPerOrder || 500);
              const pts = Math.min(Math.floor(total / 10) * earnRate, maxEarn);
              if (pts <= 0) return null;
              return (
                <div className="rounded-xl border border-purple-200 overflow-hidden mb-3">
                  <div className="flex items-center gap-2 px-3 py-2"
                    style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}>
                    <svg className="w-3.5 h-3.5 text-yellow-300 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                    </svg>
                    <p className="text-[11px] font-bold text-white">You'll earn reward points!</p>
                  </div>
                  <div className="bg-purple-50 px-3 py-2 flex items-center justify-between">
                    <p className="text-[11px] text-purple-700">Points from this order</p>
                    <div className="text-right">
                      <p className="text-xs font-bold text-purple-800">+{pts} pts</p>
                      {loyaltyInfo.rupeesPerPoint > 0 && (
                        <p className="text-[10px] text-purple-500">
                          ≈ ₹{(pts * Number(loyaltyInfo.rupeesPerPoint)).toFixed(0)} value
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Prepaid discount nudge — compact, above checkout */}
            {prepaidDiscountPreview > 0 && (
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2 mb-3">
                <span className="text-base shrink-0">💳</span>
                <p className="text-xs font-medium text-purple-800">
                  Save <span className="font-bold text-purple-700">{fmtPrice(prepaidDiscountPreview)}</span> with online payment!
                </p>
              </div>
            )}
            <button
              onClick={openCheckout}
              className="w-full py-3.5 rounded-full bg-primary hover:bg-primary-dark text-white font-bold text-sm
                         transition-colors flex items-center justify-center gap-2 mb-2.5 shadow-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
              Secure Checkout
            </button>
            <button
              onClick={() => { setIsDrawerOpen(false); navigate('/cart'); }}
              className="w-full py-2.5 rounded-full border-2 border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-all"
            >
              View Full Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
